import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import {
  Popover,
  Table,
  Tag,
  Button,
  message,
  Modal,
  DatePicker,
  Tooltip as AntdTooltip,
  Select,
  Card,
} from 'antd'
import { Auth } from 'aws-amplify'
import { API } from 'src/utils/interceptor'
import { QuestionCircleOutlined, BulbOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import moment from 'moment'
import FadeIn from 'src/components/styled/FadeIn'
import { visibleHorizontalScroll } from 'src/components/styled/horizontalScroll'
import Title from 'src/branding/Title'
import SearchCustomer from 'src/screens/overview/customers/SearchCustomer'
import InviteCustomerModal from './InviteCustomerModal'
import InviteConfirmDetails from './InviteConfirmDetails'
import InviteOutcomeModal from './InviteOutcomeModal'
import CustomerProgressDrawer from './CustomerProgressDrawer'
import ResendInviteConfirmModal from './ResendInviteConfirmModal'
import {
  saveInviteProgressAttempt,
  readInviteProgressAttempt,
  getInviteResendGate,
} from './inviteProgressStorage'
import { INVITE_RECOVERY } from './inviteRecoveryCopy'
import {
  trackSupplierInviteFunnel,
  SUPPLIER_INVITE_FUNNEL,
} from './supplierInviteAnalytics'
import CreateNewUserModal from 'src/components/CreateNewUserModal'
import EditVenueModal from './EditVenueModal'
import VenueUsersModal from './VenueUsersModal'
import LostProductsModal from './LostProductsModal'
import SalesAdvisorModal from './SalesAdvisorModal'
import { SalesAdvisorMark } from 'src/components/icons/SalesAdvisorIcon'
import { SALES_ADVISOR_CTA } from './salesAdvisorCopy'
import CustomerSummary from 'src/screens/app/components/CustomerSummary'
import {
  adminEditCustomer,
  fetchAllCustomers,
} from 'src/features/CustomerSlice'

const Customers = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const customers = useSelector(state => state.customer?.customers || [])
  const searchTitle = useSelector(state => state.customerSearch.title)
  const searchLoading = useSelector(state => state.customerSearch.loading)
  const account = useSelector(state => state.auth.account)
  const pageLoading = useSelector(state => state.customer.isLoading)

  // Get all supplier IDs (all price files in the supplier group)
  const supplierIds = useMemo(() => {
    return account?.supplier?.map(s => s._id).filter(Boolean) || []
  }, [account])

  // Use first supplier ID for API calls that need a single supplier ID
  const supplierId = supplierIds[0]

  // State for dismissable info cards
  const [showInviteMoreCard, setShowInviteMoreCard] = useState(true)
  const [showProTipCard, setShowProTipCard] = useState(true)

  const priceFileLookup = useMemo(() => {
    const supplierList = account?.supplier || []
    return supplierList.reduce((acc, supplier) => {
      const label = [supplier.title, supplier.secondTitle]
        .filter(Boolean)
        .join(' ')
      acc[supplier._id] = label
      return acc
    }, {})
  }, [account])

  // Brand/display name for the inviting supplier — never the price-file label
  const supplierDisplayName =
    account?.supplier?.[0]?.title || account?.supplierTitle || 'Your supplier'

  const [editingVenue, setEditingVenue] = useState(null)
  const [inviteModalVisible, setInviteModalVisible] = useState(false)
  const [inviteCustomerConfirmVisible, setInviteCustomerConfirmVisible] =
    useState(false)
  const [pendingInviteCustomer, setPendingInviteCustomer] = useState(null)
  const [inviteCustomerSubmitting, setInviteCustomerSubmitting] =
    useState(false)
  const [showCreateCustomerUserModal, setShowCreateCustomerUserModal] =
    useState(false)
  const [pendingNewCustomerUser, setPendingNewCustomerUser] = useState(null)
  const [creatingCustomerUser, setCreatingCustomerUser] = useState(false)
  const [createCustomerUserError, setCreateCustomerUserError] = useState('')
  const [existingRelationshipMatch, setExistingRelationshipMatch] =
    useState(null)
  const [inviteOutcome, setInviteOutcome] = useState(null)
  const [inviteOutcomeVisible, setInviteOutcomeVisible] = useState(false)
  const [inviteResending, setInviteResending] = useState(false)
  const [progressCustomer, setProgressCustomer] = useState(null)
  const [progressDrawerVisible, setProgressDrawerVisible] = useState(false)
  const [resendConfirmDraft, setResendConfirmDraft] = useState(null)
  const [resendConfirmVisible, setResendConfirmVisible] = useState(false)

  // Open existing invite flow when arriving from Orders (or ?invite=1)
  useEffect(() => {
    const params = new URLSearchParams(location.search || '')
    const shouldOpenInvite =
      Boolean(location.state?.openInvite) || params.get('invite') === '1'
    if (!shouldOpenInvite) return

    setInviteModalVisible(true)
    trackSupplierInviteFunnel({
      eventName: SUPPLIER_INVITE_FUNNEL.FORM_OPENED,
      supplierId,
      metadata: { surface: 'orders_deeplink' },
    })
    history.replace('/supplier/customers')
  }, [history, location.search, location.state, supplierId])

  const [usersModalVisible, setUsersModalVisible] = useState(false)
  const [selectedVenue, setSelectedVenue] = useState(null)
  const dateDisplayFormat = 'DD/MM/YYYY'
  const dateQueryFormat = 'YYYY-MM-DD'
  const defaultRange = [moment().subtract(90, 'days'), moment()]
  const [orderRange, setOrderRange] = useState(defaultRange)
  const [orderStats, setOrderStats] = useState({})
  const [orderStatsLoading, setOrderStatsLoading] = useState(false)
  const [allTimeLastOrders, setAllTimeLastOrders] = useState({})
  const [statusFilter, setStatusFilter] = useState('all')

  // Lost Products state
  const [lostProductsCounts, setLostProductsCounts] = useState({})
  const [lostProductsCountsLoading, setLostProductsCountsLoading] =
    useState(false)
  const [lostProductsCountsError, setLostProductsCountsError] = useState(false)
  const [lostProductsModalVisible, setLostProductsModalVisible] =
    useState(false)
  const [selectedVenueForLostProducts, setSelectedVenueForLostProducts] =
    useState(null)
  const [salesAdvisorVisible, setSalesAdvisorVisible] = useState(false)
  const [selectedVenueForSalesAdvisor, setSelectedVenueForSalesAdvisor] =
    useState(null)

  useEffect(() => {
    if (supplierIds.length > 0) {
      dispatch(fetchAllCustomers(supplierIds))
    }
  }, [dispatch, supplierIds])

  // Fetch lost products counts from backend
  useEffect(() => {
    const venueIds = (customers || [])
      .map(customer => customer?._id || customer?.venueId)
      .filter(Boolean)

    if (
      supplierIds.length === 0 ||
      venueIds.length === 0 ||
      customers.length === 0
    ) {
      setLostProductsCounts({})
      setLostProductsCountsLoading(false)
      setLostProductsCountsError(false)
      return
    }

    let isCancelled = false

    const fetchLostProductsCounts = async () => {
      setLostProductsCountsLoading(true)
      setLostProductsCountsError(false)
      try {
        const response = await API.post('api2', 'venues/lost-products/counts', {
          body: { venueIds, supplierIds },
        })

        if (!isCancelled) {
          if (response?.success && response?.data) {
            setLostProductsCounts(response.data)
            setLostProductsCountsError(false)
          } else {
            throw new Error('Invalid response format')
          }
        }
      } catch (error) {
        console.error('Failed to fetch lost products counts:', error)
        if (!isCancelled) {
          setLostProductsCountsError(true)
          message.error(
            'Unable to load lost products counts. Please try again.',
          )
        }
      } finally {
        if (!isCancelled) {
          setLostProductsCountsLoading(false)
        }
      }
    }

    fetchLostProductsCounts()

    return () => {
      isCancelled = true
    }
  }, [supplierIds, customers])

  // Client-side filtering: search by venue name OR account number
  // This is faster and more reliable than backend search, and we already have all the data
  const displayCustomer = useMemo(() => {
    const baseData = customers || []

    if (!searchTitle.trim()) {
      return baseData
    }

    const searchTerm = searchTitle.toLowerCase().trim()
    return baseData.filter(customer => {
      const venueName = (customer.venueTitle || '').toLowerCase()
      const accountNumber = (customer.accountNumber || '').toLowerCase()

      return (
        venueName.includes(searchTerm) || accountNumber.includes(searchTerm)
      )
    })
  }, [customers, searchTitle])

  const resetInviteCustomerState = () => {
    setPendingInviteCustomer(null)
    setInviteCustomerConfirmVisible(false)
    setInviteCustomerSubmitting(false)
    setExistingRelationshipMatch(null)
  }

  const showInviteOutcome = outcome => {
    if (outcome?.venueId) {
      saveInviteProgressAttempt(outcome.venueId, {
        email: outcome.email,
        emailDispatch: outcome.emailDispatch,
        journey: outcome.journey,
        venueName: outcome.venueName,
        contactName: outcome.contactName,
        priceFileId: outcome.priceFileId,
      })
      // Persisted create/email events are written by the API — avoid double-count
    }
    setInviteOutcome(outcome)
    setInviteOutcomeVisible(true)
    setInviteModalVisible(false)
    setShowCreateCustomerUserModal(false)
    setPendingNewCustomerUser(null)
    resetInviteCustomerState()
    dispatch(fetchAllCustomers(supplierIds))
  }

  const closeInviteOutcome = () => {
    setInviteOutcomeVisible(false)
    setInviteOutcome(null)
    setInviteResending(false)
  }

  const handleInviteOutcomeViewProgress = () => {
    closeInviteOutcome()
    // Already on Customers — refresh list and scroll toward table
    dispatch(fetchAllCustomers(supplierIds))
    const table = document.getElementById('tbl-customers')
    if (table?.scrollIntoView) {
      table.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const handleInviteAnotherCustomer = () => {
    closeInviteOutcome()
    setInviteModalVisible(true)
  }

  const resendExistingInviteEmail = async ({
    venueId,
    priceFileId,
    email,
    venueName,
    contactName,
  }) => {
    if (!venueId || !priceFileId) {
      message.error(INVITE_RECOVERY.OUTCOME_UNCLEAR)
      return null
    }
    const response = await API.post(
      'api2',
      `suppliers/${supplierId}/customers/invite-existing`,
      {
        body: {
          resendEmail: true,
          venueId,
          priceFileId,
          email,
          venueName,
          contactName: contactName || '',
        },
      },
    )
    return response?.data?.emailDispatch || 'failed'
  }

  const assertResendAllowed = venueId => {
    const gate = getInviteResendGate(venueId)
    if (!gate.allowed) {
      message.warning(INVITE_RECOVERY.RESEND_TOO_SOON(gate.waitSeconds))
      return false
    }
    return true
  }

  const openResendConfirm = draft => {
    if (!draft?.venueId) {
      message.error(INVITE_RECOVERY.OUTCOME_UNCLEAR)
      return
    }
    if (draft.journey === 'new') {
      message.info(INVITE_RECOVERY.NEW_USER_RESEND_UNAVAILABLE)
      return
    }
    if (!draft.email) {
      message.error(INVITE_RECOVERY.OUTCOME_UNCLEAR)
      return
    }
    if (!assertResendAllowed(draft.venueId)) return
    setResendConfirmDraft(draft)
    setResendConfirmVisible(true)
  }

  const closeResendConfirm = (force = false) => {
    if (inviteResending && !force) return
    setResendConfirmVisible(false)
    setResendConfirmDraft(null)
  }

  /** Request resend from outcome modal — confirm recipient first */
  const handleResendInviteEmail = () => {
    if (!inviteOutcome?.venueId || !inviteOutcome?.priceFileId) {
      message.error(INVITE_RECOVERY.OUTCOME_UNCLEAR)
      return
    }
    openResendConfirm({
      ...inviteOutcome,
      source: 'outcome',
    })
  }

  /** Request resend from progress drawer — confirm recipient first */
  const handleResendFromProgress = customer => {
    const venueId = customer?._id || customer?.venueId
    const attempt = readInviteProgressAttempt(venueId)
    if (!attempt) {
      message.info(INVITE_RECOVERY.HISTORY_UNAVAILABLE)
      return
    }
    openResendConfirm({
      venueId,
      priceFileId: attempt.priceFileId || customer.supplierId,
      email: attempt.email || customer.emailAddress,
      venueName: attempt.venueName || customer.venueTitle,
      contactName: attempt.contactName || customer.contactName || '',
      accountNumber: customer.accountNumber,
      journey: attempt.journey || 'existing',
      source: 'progress',
      customer,
    })
  }

  const confirmResendInvitation = async () => {
    const draft = resendConfirmDraft
    if (!draft) return
    if (!assertResendAllowed(draft.venueId)) return

    setInviteResending(true)
    try {
      const emailDispatch = await resendExistingInviteEmail(draft)
      const attemptRecord = {
        email: draft.email,
        emailDispatch: emailDispatch || 'failed',
        journey: draft.journey || 'existing',
        venueName: draft.venueName,
        contactName: draft.contactName,
        priceFileId: draft.priceFileId,
        resent: true,
        at: new Date().toISOString(),
      }
      saveInviteProgressAttempt(draft.venueId, attemptRecord)

      // Resend analytics are written by the API (supplier_invite_resent)

      if (emailDispatch === 'sent') {
        message.success('Invitation email resent.')
        if (draft.source === 'outcome') {
          setInviteOutcome(prev =>
            prev ? { ...prev, emailDispatch: 'sent' } : prev,
          )
        }
        if (draft.source === 'progress' && draft.customer) {
          setProgressCustomer({ ...draft.customer })
        }
        closeResendConfirm(true)
      } else {
        message.error(INVITE_RECOVERY.CUSTOMER_ADDED_EMAIL_FAILED)
      }
    } catch (error) {
      const status = error.response?.status
      if (!error.response || status >= 500) {
        message.error(INVITE_RECOVERY.OUTCOME_UNCLEAR)
      } else {
        message.error(
          error.response?.data?.message || INVITE_RECOVERY.NO_INVITE_CREATED,
        )
      }
      console.error('Resend invite error:', error)
    } finally {
      setInviteResending(false)
    }
  }

  const findExistingCustomerRelationship = accountNumber => {
    const normalized = String(accountNumber || '')
      .trim()
      .toUpperCase()
    if (!normalized) return null
    return (
      (customers || []).find(
        customer =>
          String(customer.accountNumber || '')
            .trim()
            .toUpperCase() === normalized,
      ) || null
    )
  }

  const openExistingCustomerRecord = match => {
    if (!match) return
    message.info(INVITE_RECOVERY.ALREADY_CONNECTED)
    setExistingRelationshipMatch(null)
    setInviteModalVisible(false)
    resetInviteCustomerState()
    setPendingNewCustomerUser(null)
    setShowCreateCustomerUserModal(false)
    handleOpenUsersModal(match)
  }

  const inviteFailureMessage = error => {
    const status = error?.response?.status
    if (!error?.response || status >= 500) {
      return INVITE_RECOVERY.OUTCOME_UNCLEAR
    }
    return error.response?.data?.message || INVITE_RECOVERY.NO_INVITE_CREATED
  }

  const disabledOrderDate = current =>
    current && current > moment().endOf('day')

  const handleOrderRangeChange = dates => {
    if (!dates || dates.length < 2) {
      setOrderRange([null, null])
      return
    }
    setOrderRange(dates)
  }

  const formatCurrencyFromPence = value => {
    if (!Number.isFinite(value)) {
      return '—'
    }
    return `£${Number(value).toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }

  const formatLastOrderDate = dateValue => {
    if (!dateValue) return '—'
    const parsed = moment(dateValue)
    if (!parsed.isValid()) return '—'
    return parsed.format('DD/MM/YY')
  }

  useEffect(() => {
    const venueIds = (customers || [])
      .map(customer => customer?._id || customer?.venueId)
      .filter(Boolean)

    if (
      supplierIds.length === 0 ||
      !orderRange?.[0] ||
      !orderRange?.[1] ||
      venueIds.length === 0
    ) {
      setOrderStats({})
      setOrderStatsLoading(false)
      return
    }

    let isCancelled = false

    const fetchStats = async () => {
      setOrderStatsLoading(true)
      try {
        const start = orderRange[0].format(dateQueryFormat)
        const end = orderRange[1].format(dateQueryFormat)

        console.log(
          `📊 Fetching date-range stats from backend (${start} to ${end})...`,
        )

        // Use new optimized backend aggregation endpoint
        const response = await API.post('api2', 'suppliers/date-range-stats', {
          body: {
            venueIds,
            supplierIds,
            startDate: start,
            endDate: end,
          },
        })

        if (response?.success && response?.data) {
          console.log(
            '✅ Date-range stats fetched:',
            Object.keys(response.data).length,
            'venues',
          )

          if (!isCancelled) {
            setOrderStats(response.data)
          }
        } else {
          console.error('Failed to fetch stats:', response?.message)
          if (!isCancelled) {
            setOrderStats({})
            message.error('Unable to load order metrics. Please try again.')
          }
        }
      } catch (error) {
        console.error('Failed to fetch venue order stats:', error)
        if (!isCancelled) {
          setOrderStats({})
          message.error('Unable to load order metrics. Please try again.')
        }
      } finally {
        if (!isCancelled) {
          setOrderStatsLoading(false)
        }
      }
    }

    fetchStats()

    return () => {
      isCancelled = true
    }
  }, [supplierIds, customers, orderRange])

  useEffect(() => {
    // Only proceed if we have account ID and it's a supplier admin
    if (!account?._id || !account?.supplierAdmin) {
      setAllTimeLastOrders({})
      return
    }

    let isCancelled = false

    const fetchAllTimeStats = async () => {
      try {
        console.log('📊 Fetching supplier stats from backend...')
        const response = await API.get(
          'api2',
          `suppliers/stats?accountId=${account._id}`,
        )

        if (response?.success && response?.data) {
          console.log('✅ Stats fetched:', response.data)

          // Transform the stats data into the format expected by the UI
          // venueStats is an array with venue, supplier info, and last order date
          const lastOrders = {}

          response.data.venueStats?.forEach(stat => {
            const venueId = stat.venue?._id || stat.venue
            if (venueId && stat.lastOrderDate) {
              // Store the most recent order date for each venue across all suppliers
              if (
                !lastOrders[venueId] ||
                new Date(stat.lastOrderDate) > new Date(lastOrders[venueId])
              ) {
                lastOrders[venueId] = stat.lastOrderDate
              }
            }
          })

          if (!isCancelled) {
            setAllTimeLastOrders(lastOrders)
            console.log('📈 All-time stats applied to UI:', lastOrders)
          }
        } else if (response?.success && response?.data === null) {
          console.log('ℹ️ No stats calculated yet for this account')
          setAllTimeLastOrders({})
        } else {
          console.error('Failed to fetch stats:', response?.message)
          setAllTimeLastOrders({})
        }
      } catch (error) {
        console.error('Failed to fetch all-time stats:', error)
        // Fallback: Set empty object to prevent UI errors
        if (!isCancelled) {
          setAllTimeLastOrders({})
        }
      }
    }

    fetchAllTimeStats()

    return () => {
      isCancelled = true
    }
  }, [account?._id, account?.supplierAdmin])

  const handleCreateCustomerUser = async userData => {
    if (
      !pendingNewCustomerUser?.payload ||
      !pendingNewCustomerUser?.mappedPayload
    ) {
      return
    }

    setCreatingCustomerUser(true)
    setCreateCustomerUserError('')
    let cognitoCreated = false
    try {
      await Auth.signUp({
        username: pendingNewCustomerUser.mappedPayload.email,
        password: userData.password,
        attributes: {
          name: userData.name,
        },
      })
      cognitoCreated = true

      const response = await API.post(
        'api2',
        `suppliers/${supplierId}/customers/invite-new`,
        {
          body: {
            userName: userData.name,
            userEmail: pendingNewCustomerUser.mappedPayload.email,
            userPassword: userData.password,
            payload: pendingNewCustomerUser.mappedPayload,
          },
        },
      )

      const data = response?.data || {}
      if (data.alreadyConnected || data.relationshipReused) {
        handleAlreadyConnectedFromApi(
          { data, code: 'RELATIONSHIP_EXISTS' },
          pendingNewCustomerUser.mappedPayload,
        )
        setShowCreateCustomerUserModal(false)
        setPendingNewCustomerUser(null)
        return
      }

      showInviteOutcome({
        email: pendingNewCustomerUser.mappedPayload.email,
        venueName: pendingNewCustomerUser.mappedPayload.venueName,
        contactName:
          pendingNewCustomerUser.mappedPayload.contactName || userData.name,
        venueId: data.venueId,
        priceFileId: pendingNewCustomerUser.mappedPayload.priceFileId,
        accountNumber: pendingNewCustomerUser.mappedPayload.accountNumber,
        emailDispatch: data.emailDispatch || 'sent',
        journey: 'new',
      })
    } catch (error) {
      const status = error.response?.status
      const code = error.response?.data?.code || ''
      if (
        status === 409 ||
        code === 'RELATIONSHIP_EXISTS' ||
        code === 'RELATIONSHIP_EXISTS_RECIPIENT_MISMATCH'
      ) {
        handleAlreadyConnectedFromApi(
          error,
          pendingNewCustomerUser?.mappedPayload,
        )
        setShowCreateCustomerUserModal(false)
        setPendingNewCustomerUser(null)
        setCreateCustomerUserError('')
        return
      }

      let errorMessage = INVITE_RECOVERY.NO_INVITE_CREATED
      if (error.code === 'UsernameExistsException') {
        errorMessage =
          'A user with this email already exists. Go back and invite them as an existing OMS user, or open their record if they are already connected.'
      } else if (error.code === 'InvalidPasswordException') {
        errorMessage = error.message || 'Password does not meet requirements.'
      } else if (cognitoCreated) {
        // Cognito account exists; venue invite may still be retried as existing user
        errorMessage =
          'Their OMS login was created, but the invitation could not be finished. Close this and invite them again as an existing OMS user with the same email.'
      } else if (!error.response || error.response?.status >= 500) {
        errorMessage = INVITE_RECOVERY.OUTCOME_UNCLEAR
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      }
      setCreateCustomerUserError(errorMessage)
      console.error('Create customer user error:', error)
    } finally {
      setCreatingCustomerUser(false)
    }
  }

  const handleCreateCustomerUserCancel = () => {
    setShowCreateCustomerUserModal(false)
    setPendingNewCustomerUser(null)
    setCreateCustomerUserError('')
  }

  const parseAddress = addressString => {
    if (!addressString) {
      return {
        addressLine1: '',
        addressLine2: '',
        addressCity: '',
        addressPostCode: '',
      }
    }
    const parts = addressString.split(',').map(part => part.trim())
    return {
      addressLine1: parts[0] || '',
      addressLine2: parts[1] || '',
      addressCity: parts[2] || '',
      addressPostCode: parts[3] || '',
    }
  }

  // Handle invite customer submission
  const handleInviteCustomer = async payload => {
    trackSupplierInviteFunnel({
      eventName: SUPPLIER_INVITE_FUNNEL.SUBMIT_ATTEMPTED,
      supplierId,
      metadata: { surface: 'invite_modal' },
    })

    const addressParts = parseAddress(
      payload.advanced?.address ||
        [
          payload.advanced?.addressLine1,
          payload.advanced?.addressLine2,
          payload.advanced?.addressCity,
          payload.advanced?.addressPostCode,
        ]
          .filter(Boolean)
          .join(', '),
    )

    const formattedPayload = {
      venueName: payload.venue_name.trim(),
      contactName: payload.contact_name?.trim() || '',
      contactPhone: payload.contact_phone || '',
      email: payload.email.trim().toLowerCase(),
      accountNumber: payload.account_number.trim(),
      priceFileId: payload.price_file_id,
      supplierName: supplierDisplayName,
      advanced: {
        minOrder: payload.advanced?.min_order
          ? Math.round(parseFloat(payload.advanced?.min_order) * 100)
          : null,
        deliveryDays: payload.advanced?.delivery_days || [],
        cutOff: payload.advanced?.cut_off || null,
        deliveryFee: payload.advanced?.delivery_fee
          ? Math.round(parseFloat(payload.advanced?.delivery_fee) * 100)
          : null,
        ...addressParts,
      },
    }

    const priceFileName =
      priceFileLookup[formattedPayload.priceFileId] || 'Selected price file'

    // Possible existing relationship — explain before creating another venue
    const existingMatch = findExistingCustomerRelationship(
      formattedPayload.accountNumber,
    )
    if (existingMatch) {
      setExistingRelationshipMatch({
        customer: existingMatch,
        draft: {
          ...formattedPayload,
          priceFileName,
        },
      })
      return
    }

    try {
      await API.post('api2', 'admin/checkEmailExists', {
        body: {
          email: formattedPayload.email,
        },
      })
      setPendingInviteCustomer({
        ...formattedPayload,
        priceFileName,
      })
      setInviteCustomerConfirmVisible(true)
    } catch (error) {
      if (error.response?.status === 404) {
        setPendingNewCustomerUser({
          payload,
          mappedPayload: {
            ...formattedPayload,
            priceFileName,
          },
        })
        setShowCreateCustomerUserModal(true)
        setCreateCustomerUserError('')
      } else {
        message.error(inviteFailureMessage(error))
      }
      console.error('Invite error:', error)
    }
  }

  const handleAlreadyConnectedFromApi = (errorOrData, draft) => {
    const data = errorOrData?.response?.data?.data || errorOrData?.data || {}
    const code = errorOrData?.response?.data?.code || errorOrData?.code || ''
    message.info(INVITE_RECOVERY.ALREADY_CONNECTED)

    const matchFromList = findExistingCustomerRelationship(
      draft?.accountNumber || pendingInviteCustomer?.accountNumber,
    )
    if (matchFromList) {
      setExistingRelationshipMatch({
        customer: matchFromList,
        draft: draft || pendingInviteCustomer,
      })
      resetInviteCustomerState()
      setInviteModalVisible(false)
      return
    }

    // Backend knows the venue but it may not be in the local list yet
    if (data.venueId) {
      dispatch(fetchAllCustomers(supplierIds))
    }

    if (code === 'RELATIONSHIP_EXISTS_RECIPIENT_MISMATCH') {
      message.warning(
        'This account is already connected to a different recipient. Open their record — changing recipient is not available as a recovery shortcut.',
      )
    }

    resetInviteCustomerState()
    setInviteModalVisible(false)
  }

  const sendInviteToExistingCustomer = async () => {
    if (!pendingInviteCustomer) return
    setInviteCustomerSubmitting(true)
    try {
      const response = await API.post(
        'api2',
        `suppliers/${supplierId}/customers/invite-existing`,
        {
          body: pendingInviteCustomer,
        },
      )
      const data = response?.data || {}

      // Timeout retry / identical request: may reuse existing relationship.
      // Email fail after create/reuse still uses outcome modal (recoverable).
      showInviteOutcome({
        email: pendingInviteCustomer.email,
        venueName: pendingInviteCustomer.venueName,
        contactName: pendingInviteCustomer.contactName || '',
        venueId: data.venueId,
        priceFileId: pendingInviteCustomer.priceFileId,
        accountNumber: pendingInviteCustomer.accountNumber,
        emailDispatch: data.emailDispatch || 'sent',
        journey: 'existing',
      })
    } catch (error) {
      const status = error.response?.status
      const code = error.response?.data?.code || ''
      if (
        status === 409 ||
        code === 'RELATIONSHIP_EXISTS' ||
        code === 'RELATIONSHIP_EXISTS_RECIPIENT_MISMATCH'
      ) {
        handleAlreadyConnectedFromApi(error, pendingInviteCustomer)
      } else {
        message.error(inviteFailureMessage(error))
      }
      console.error('Invite customer confirm error:', error)
    } finally {
      setInviteCustomerSubmitting(false)
    }
  }

  const handleInviteModalCancel = () => {
    setInviteModalVisible(false)
    resetInviteCustomerState()
    setPendingNewCustomerUser(null)
    setShowCreateCustomerUserModal(false)
    setCreateCustomerUserError('')
    setExistingRelationshipMatch(null)
  }

  // Handle edit venue submission
  const handleEditVenue = async (venueId, payload) => {
    try {
      const normalizeCurrency = value =>
        typeof value === 'number' && !Number.isNaN(value) ? value : null

      const deliveryDays = payload.advanced?.delivery_days || []
      const mappedPayload = {
        venueSupplierId:
          payload.venue_supplier_id !== undefined
            ? payload.venue_supplier_id
            : undefined, // undefined means "find automatically"
        accountNumber: payload.account_number,
        deliveryDays,
        minOrderValue: normalizeCurrency(payload.advanced?.min_order),
        deliveryFee: normalizeCurrency(payload.advanced?.delivery_fee),
        orderCutOffTime: payload.advanced?.cut_off || null,
        priceFileId: payload.price_file_id,
        addressLine1: payload.advanced?.addressLine1 || '',
        addressLine2: payload.advanced?.addressLine2 || '',
        addressCity: payload.advanced?.addressCity || '',
        addressPostCode: payload.advanced?.addressPostCode || '',
      }

      await dispatch(adminEditCustomer(supplierId, venueId, mappedPayload))
      message.success('Venue updated.')
      setEditingVenue(null)
      // Refresh the customers list
      dispatch(fetchAllCustomers(supplierIds))
    } catch (error) {
      message.error('Failed to update venue. Please try again.')
      console.error('Edit error:', error)
    }
  }

  const isLoading = pageLoading || searchLoading

  // Ordering activity only — not invitation / access status
  const getVenueStatus = record => {
    const venueId = record?._id || record?.venueId
    const lastOrder =
      (venueId && allTimeLastOrders[venueId]) ||
      record?.lastOrderDisplay ||
      record?.orderStats?.lastOrder
    if (!lastOrder) {
      return { key: 'no_orders', label: 'No orders yet', color: '#94a3b8' }
    }

    const daysSinceOrder = moment().diff(moment(lastOrder), 'days')
    if (daysSinceOrder <= 30) {
      return { key: 'active', label: 'Active', color: '#16a34a' }
    }

    return { key: 'inactive', label: 'Inactive', color: '#dc2626' }
  }

  const openCustomerProgress = useCallback(record => {
    setProgressCustomer(record)
    setProgressDrawerVisible(true)
  }, [])

  const closeCustomerProgress = () => {
    setProgressDrawerVisible(false)
    setProgressCustomer(null)
  }

  const handleOpenUsersModal = useCallback(venue => {
    setSelectedVenue(venue)
    setUsersModalVisible(true)
  }, [])

  const handleOpenLostProductsModal = useCallback(venue => {
    setSelectedVenueForLostProducts(venue)
    setLostProductsModalVisible(true)
  }, [])

  const handleOpenSalesAdvisor = useCallback(venue => {
    setSelectedVenueForSalesAdvisor(venue)
    setSalesAdvisorVisible(true)
  }, [])

  // Table columns definition
  const columns = useMemo(
    () => [
      {
        title: 'Sales opportunities',
        key: 'salesAdvisor',
        width: 220,
        className: 'restrictShrink insightColumn',
        onCell: () => ({
          'data-column-key': 'salesAdvisor',
          onClick: e => {
            e.stopPropagation()
            e.preventDefault()
          },
          onMouseDown: e => {
            e.stopPropagation()
            e.preventDefault()
          },
        }),
        render: (_, record) => (
          <InsightTrigger
            type='button'
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
              handleOpenSalesAdvisor(record)
            }}
          >
            <SalesAdvisorMark size={26} />
            <span>{SALES_ADVISOR_CTA}</span>
          </InsightTrigger>
        ),
      },
      {
        title: 'Lost Products',
        key: 'lostProducts',
        width: 132,
        className: 'restrictShrink',
        onCell: () => ({
          'data-column-key': 'lostProducts',
          onClick: e => {
            // Completely stop row click events in this column
            e.stopPropagation()
            e.preventDefault()
          },
          onMouseDown: e => {
            // Stop mousedown events too
            e.stopPropagation()
            e.preventDefault()
          },
        }),
        render: (_, record) => {
          const venueId = record?._id || record?.venueId
          const count = venueId ? lostProductsCounts[venueId] : undefined
          const isLoading = lostProductsCountsLoading
          const hasError = lostProductsCountsError

          // Loading state
          if (isLoading && count === undefined) {
            return <Tag style={{ margin: 0 }}>…</Tag>
          }

          // Error state
          if (hasError && count === undefined) {
            return <Tag style={{ margin: 0, color: '#64748b' }}>—</Tag>
          }

          // Count = 0 (OMS green, non-clickable) - same size as other badges
          if (count === 0) {
            return <ZeroBadge>0</ZeroBadge>
          }

          // Count > 0 (clickable button with conditional colors)
          if (count > 0) {
            // Red if count > 5, amber if 0 < count <= 5
            const badgeColor = count > 5 ? '#dc2626' : '#f59e0b' // red or amber
            const badgeHoverColor = count > 5 ? '#b91c1c' : '#d97706' // darker red or darker amber

            return (
              <BadgeButton
                $badgeColor={badgeColor}
                $badgeHoverColor={badgeHoverColor}
                onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                  handleOpenLostProductsModal(record)
                }}
              >
                {count}
              </BadgeButton>
            )
          }

          // Fallback (shouldn't happen, but just in case)
          return <Tag style={{ margin: 0, color: '#64748b' }}>—</Tag>
        },
        sorter: (a, b) => {
          const aId = a?._id || a?.venueId
          const bId = b?._id || b?.venueId
          const aCount = aId ? lostProductsCounts[aId] || 0 : 0
          const bCount = bId ? lostProductsCounts[bId] || 0 : 0
          return aCount - bCount
        },
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: 'Venue',
        dataIndex: 'venueTitle',
        key: 'venueTitle',
        width: 180,
        className: 'customers-clip-col',
        render: title => (
          <CellClip title={title || ''}>{title || '—'}</CellClip>
        ),
        sorter: (a, b) =>
          String(a.venueTitle || '').localeCompare(String(b.venueTitle || '')),
        sortDirections: ['ascend', 'descend'],
      },
      {
        title: 'Account no.',
        dataIndex: 'accountNumber',
        key: 'accountNumber',
        width: 140,
        className: 'customers-clip-col',
        render: value => (
          <CellClip title={value || ''}>{value || '—'}</CellClip>
        ),
        sorter: (a, b) =>
          String(a.accountNumber || '').localeCompare(
            String(b.accountNumber || ''),
          ),
        sortDirections: ['ascend', 'descend'],
      },
      {
        title: 'Recipient',
        key: 'recipient',
        width: 200,
        className: 'customers-clip-col',
        render: (_, record) => {
          const email = record.emailAddress || record.contactEmail || '—'
          return (
            <CellClip title={email} style={{ color: '#111827' }}>
              {email}
            </CellClip>
          )
        },
        sorter: (a, b) =>
          String(a.emailAddress || a.contactEmail || '').localeCompare(
            String(b.emailAddress || b.contactEmail || ''),
          ),
        sortDirections: ['ascend', 'descend'],
      },
      {
        title: 'Price file',
        key: 'priceFile',
        width: 170,
        className: 'customers-clip-col',
        render: (_, record) => {
          const fullTitle = [record.titlePriceFile, record.secondTitlePriceFile]
            .filter(Boolean)
            .join(' ')
          const label = fullTitle || '—'
          return <CellClip title={label}>{label}</CellClip>
        },
        sorter: (a, b) => {
          const aTitle = [a.titlePriceFile, a.secondTitlePriceFile]
            .filter(Boolean)
            .join(' ')
          const bTitle = [b.titlePriceFile, b.secondTitlePriceFile]
            .filter(Boolean)
            .join(' ')
          return String(aTitle || '').localeCompare(String(bTitle || ''))
        },
        sortDirections: ['ascend', 'descend'],
      },
      {
        title: (
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            Ordering
            <AntdTooltip
              title={
                <div>
                  <strong>Ordering activity</strong> (not invitation status)
                  <ul style={{ paddingLeft: 16, margin: '4px 0 0' }}>
                    <li>Active: order in the last 30 days</li>
                    <li>Inactive: last order over 30 days ago</li>
                    <li>No orders yet: connected but has not ordered</li>
                  </ul>
                </div>
              }
            >
              <QuestionCircleOutlined style={{ color: '#64748b' }} />
            </AntdTooltip>
          </span>
        ),
        key: 'orderingStatus',
        width: 150,
        className: 'restrictShrink',
        render: (_, record) => {
          const { label, color } = getVenueStatus(record)
          return (
            <Tag color={color} style={{ margin: 0 }}>
              {label}
            </Tag>
          )
        },
        sorter: (a, b) => {
          const statusPriority = {
            Active: 3,
            Inactive: 2,
            'No orders yet': 1,
          }
          return (
            (statusPriority[getVenueStatus(a).label] || 0) -
            (statusPriority[getVenueStatus(b).label] || 0)
          )
        },
        sortDirections: ['descend', 'ascend'],
        defaultSortOrder: 'descend',
      },
      {
        title: 'No. of Orders',
        key: 'orderCount',
        width: 144,
        className: 'restrictShrink',
        render: (_, record) =>
          record.orderStats?.orderCount ?? (
            <span style={{ color: '#64748b' }}>—</span>
          ),
        sorter: (a, b) =>
          (a.orderStats?.orderCount || 0) - (b.orderStats?.orderCount || 0),
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: 'Last order',
        key: 'lastOrder',
        width: 112,
        className: 'restrictShrink',
        render: (_, record) => (
          <span style={{ color: '#111827' }}>
            {formatLastOrderDate(record.lastOrderDisplay)}
          </span>
        ),
        sorter: (a, b) => {
          const aDate = a.lastOrderDisplay
            ? new Date(a.lastOrderDisplay).getTime()
            : 0
          const bDate = b.lastOrderDisplay
            ? new Date(b.lastOrderDisplay).getTime()
            : 0
          return aDate - bDate
        },
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: 'Spend',
        key: 'spend',
        width: 140,
        className: 'restrictShrink',
        render: (_, record) => (
          <span style={{ color: '#111827' }}>
            {formatCurrencyFromPence(record.orderStats?.spend)}
          </span>
        ),
        sorter: (a, b) =>
          (a.orderStats?.spend || 0) - (b.orderStats?.spend || 0),
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: 'Progress',
        key: 'nextAction',
        width: 78,
        fixed: 'right',
        className: 'customer-action-col',
        onCell: () => ({
          className: 'customer-action-cell',
          'data-column-key': 'nextAction',
          onClick: e => {
            e.stopPropagation()
          },
        }),
        render: (_, record) => (
          <Button
            type='link'
            size='small'
            id={`btn-customer-progress-${record._id || record.accountNumber}`}
            onClick={e => {
              e.stopPropagation()
              openCustomerProgress(record)
            }}
          >
            Progress
          </Button>
        ),
      },
      {
        title: 'View',
        key: 'users',
        width: 56,
        fixed: 'right',
        className: 'customer-action-col',
        onCell: () => ({
          className: 'customer-action-cell',
          onClick: e => {
            e.stopPropagation()
          },
        }),
        render: (_, record) => (
          <Button
            type='link'
            size='small'
            onClick={e => {
              e.stopPropagation()
              handleOpenUsersModal(record)
            }}
          >
            View
          </Button>
        ),
      },
      {
        title: 'Edit',
        key: 'edit',
        width: 52,
        fixed: 'right',
        className: 'customer-action-col',
        onCell: () => ({
          className: 'customer-action-cell',
          onClick: e => {
            e.stopPropagation()
          },
        }),
        render: (_, record) => (
          <Button
            type='link'
            size='small'
            onClick={e => {
              e.stopPropagation()
              setEditingVenue(record)
            }}
          >
            Edit
          </Button>
        ),
      },
    ],
    [
      handleOpenUsersModal,
      openCustomerProgress,
      allTimeLastOrders,
      lostProductsCounts,
      lostProductsCountsLoading,
      lostProductsCountsError,
      handleOpenLostProductsModal,
      handleOpenSalesAdvisor,
    ],
  )

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  // Prepare table data with keys
  const tableData = useMemo(() => {
    return displayCustomer.map(customer => ({
      key: customer._id || customer.accountNumber,
      ...customer,
    }))
  }, [displayCustomer])

  const tableDataWithStats = useMemo(() => {
    return tableData.map(customer => {
      const venueId = customer?._id || customer?.venueId
      const stats = venueId ? orderStats[venueId] || {} : {}
      return {
        ...customer,
        orderStats: stats,
        lastOrderDisplay: allTimeLastOrders[venueId] || stats.lastOrder || null,
      }
    })
  }, [tableData, orderStats, allTimeLastOrders])

  const filteredTableData = useMemo(() => {
    if (statusFilter === 'all') {
      return tableDataWithStats
    }
    // Support legacy 'pending' filter value as 'no_orders'
    const filterKey = statusFilter === 'pending' ? 'no_orders' : statusFilter
    return tableDataWithStats.filter(record => {
      return getVenueStatus(record).key === filterKey
    })
  }, [tableDataWithStats, statusFilter, allTimeLastOrders])

  // Compute summary metrics from filteredTableData
  const summaryMetrics = useMemo(() => {
    const thirtyDaysAgo = moment().subtract(30, 'days')

    let activeCount = 0
    let newCount = 0
    let totalSpend = 0

    filteredTableData.forEach(record => {
      // Active customers
      if (getVenueStatus(record).label === 'Active') {
        activeCount++
      }

      // New customers (last 30 days) - extract from _id timestamp
      if (record._id && record._id.length >= 8) {
        try {
          const createdAtFromId = new Date(
            parseInt(record._id.substring(0, 8), 16) * 1000,
          )
          if (createdAtFromId >= thirtyDaysAgo.toDate()) {
            newCount++
          }
        } catch (error) {
          // Skip invalid _id
        }
      }

      // Total spend (already in pounds)
      const spend = Number(record.orderStats?.spend || 0)
      if (Number.isFinite(spend) && spend >= 0) {
        totalSpend += spend
      }
    })

    // Format spend using the same formatter as the table
    const formattedSpend = formatCurrencyFromPence(totalSpend)

    return {
      activeCustomers: activeCount,
      newCustomers30Days: newCount,
      totalSpend: formattedSpend,
    }
  }, [filteredTableData, allTimeLastOrders])

  // Pagination config
  const paginationConfig = useMemo(
    () => ({
      current: currentPage,
      pageSize,
      total: filteredTableData.length,
      showSizeChanger: true,
      pageSizeOptions: ['25', '50', '100'],
      onChange: (page, size) => {
        setCurrentPage(page)
        setPageSize(size)
      },
      onShowSizeChange: (_, size) => {
        setPageSize(size)
        setCurrentPage(1)
      },
    }),
    [filteredTableData.length, pageSize, currentPage],
  )

  return (
    <Wrapper>
      <Header>
        <div>
          <Title level={2}>Customers</Title>
          <HeroText>
            Invite your venues to order from you on OMS. Their orders still come
            straight to you by email or via existing integrations, and ordering
            is always free for them.
          </HeroText>
        </div>
        <HeaderActions>
          <Popover
            placement='bottomRight'
            title='How does this work with my existing orders?'
            content={
              <div>
                <p>
                  When your customers order through OMS, their orders are sent
                  to you exactly as they are today:
                </p>
                <ul>
                  <li>
                    If you receive orders by email, OMS sends them to the same
                    inbox.
                  </li>
                  <li>
                    If you use an integration, OMS routes orders through your
                    existing integration automatically.
                  </li>
                </ul>
                <p>
                  There’s no additional cost for you or your customers. If you’d
                  like to switch from email to an integration, let us know—setup
                  is free and self-serve.
                </p>
              </div>
            }
            trigger='click'
          >
            <InfoButton>
              <QuestionCircleOutlined />
              How does this work?
            </InfoButton>
          </Popover>
          <CTAButton
            id='btn-invite-customer'
            onClick={() => {
              setInviteModalVisible(true)
              trackSupplierInviteFunnel({
                eventName: SUPPLIER_INVITE_FUNNEL.FORM_OPENED,
                supplierId,
                metadata: { surface: 'customers_header' },
              })
            }}
          >
            Invite a customer
          </CTAButton>
        </HeaderActions>
      </Header>

      <SearchContainer>
        <SearchCustomer resultCount={displayCustomer.length} />
      </SearchContainer>

      <FiltersBar>
        <FilterGroup>
          <FilterLabel>Date range</FilterLabel>
          <DatePicker.RangePicker
            allowClear={false}
            value={orderRange}
            format={dateDisplayFormat}
            onChange={handleOrderRangeChange}
            disabledDate={disabledOrderDate}
            style={{ minWidth: 260 }}
          />
          <FilterHelper>
            Metrics update based on the selected window (spend + order count).
          </FilterHelper>
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Ordering activity</FilterLabel>
          <Select
            value={statusFilter === 'pending' ? 'no_orders' : statusFilter}
            style={{ width: 180 }}
            onChange={value => {
              setStatusFilter(value)
              setCurrentPage(1)
            }}
          >
            <Select.Option value='all'>All customers</Select.Option>
            <Select.Option value='active'>Active</Select.Option>
            <Select.Option value='inactive'>Inactive</Select.Option>
            <Select.Option value='no_orders'>No orders yet</Select.Option>
          </Select>
        </FilterGroup>
      </FiltersBar>

      <CustomerSummary
        activeCustomers={summaryMetrics.activeCustomers}
        newCustomers30Days={summaryMetrics.newCustomers30Days}
        totalSpend={summaryMetrics.totalSpend}
      />

      <InfoCardsWrapper>
        {showInviteMoreCard && (
          <ProTipCard>
            <CloseButton onClick={() => setShowInviteMoreCard(false)}>
              ×
            </CloseButton>
            <ProTipIcon>
              <BulbOutlined style={{ fontSize: 18, color: '#f59e0b' }} />
            </ProTipIcon>
            <ProTipContent>
              <ProTipTitle>Invite More Customers?</ProTipTitle>
              <ProTipText>
                <ProTipBold>Did you know?</ProTipBold>
                <br />
                Adding new venues on OMS is completely free.
                <br />
                <br />
                If you have customers who still order by WhatsApp, email, or
                phone, you can add them here and get all their orders in one
                place — no paperwork, no chasing messages, no manual admin.
                <br />
                <br />
                Most suppliers add 2–5 extra customers once they realise how
                easy it is.
              </ProTipText>
            </ProTipContent>
          </ProTipCard>
        )}
        {showProTipCard && (
          <ProTipCard>
            <CloseButton onClick={() => setShowProTipCard(false)}>
              ×
            </CloseButton>
            <ProTipIcon>
              <BulbOutlined style={{ fontSize: 18, color: '#f59e0b' }} />
            </ProTipIcon>
            <ProTipContent>
              <ProTipTitle>Pro Tip: £0 Price File</ProTipTitle>
              <ProTipText>
                Create a £0 price file with products but no prices. Perfect for
                inviting new customers - they won&apos;t see pricing, making it
                easier to onboard them. Switch them to a priced file when ready.
              </ProTipText>
            </ProTipContent>
          </ProTipCard>
        )}
      </InfoCardsWrapper>

      <TableWrapper>
        <ScrollHint>Swipe / scroll sideways to see more columns</ScrollHint>
        <Table
          id='tbl-customers'
          dataSource={filteredTableData}
          columns={columns}
          tableLayout='fixed'
          loading={isLoading || orderStatsLoading}
          pagination={paginationConfig}
          rowKey={record => String(record._id || record.accountNumber)}
          scroll={{
            scrollToFirstRowOnChange: true,
            x: columns.reduce((total, column) => total + column.width, 0),
          }}
          locale={{
            emptyText: (
              <EmptyState>
                <strong>No customers yet</strong>
                <span>
                  Invite a customer so they can order from you on OMS with your
                  products and prices. Ordering is free for them.
                </span>
                <EmptyStateButton
                  type='button'
                  id='btn-empty-invite-customer'
                  onClick={() => {
                    setInviteModalVisible(true)
                    trackSupplierInviteFunnel({
                      eventName: SUPPLIER_INVITE_FUNNEL.FORM_OPENED,
                      supplierId,
                      metadata: { surface: 'customers_empty' },
                    })
                  }}
                >
                  Invite a customer
                </EmptyStateButton>
              </EmptyState>
            ),
          }}
          onRow={record => ({
            onClick: e => {
              // Check if click was on the Lost Products badge or in the first column
              const target = e.target
              const cell = target.closest('td')
              const columnKey = cell && cell.getAttribute('data-column-key')
              const isBadgeClick =
                target.closest('.ant-tag') ||
                target.closest('[data-lost-products-badge]') ||
                columnKey === 'lostProducts' ||
                columnKey === 'salesAdvisor'

              // Only open Users modal if click wasn't on the badge
              if (!isBadgeClick) {
                handleOpenUsersModal(record)
              }
            },
            style: { cursor: 'pointer' },
          })}
        />
      </TableWrapper>

      <InviteCustomerModal
        visible={inviteModalVisible}
        onCancel={handleInviteModalCancel}
        onSubmit={handleInviteCustomer}
      />

      <CreateNewUserModal
        visible={showCreateCustomerUserModal}
        prefilledEmail={pendingNewCustomerUser?.payload?.email}
        onCancel={handleCreateCustomerUserCancel}
        onSubmit={handleCreateCustomerUser}
        loading={creatingCustomerUser}
        error={createCustomerUserError}
        inviteSummary={
          pendingNewCustomerUser?.mappedPayload
            ? {
                supplierName:
                  pendingNewCustomerUser.mappedPayload.supplierName ||
                  supplierDisplayName,
                venueName: pendingNewCustomerUser.mappedPayload.venueName,
                recipientEmail: pendingNewCustomerUser.mappedPayload.email,
                accountNumber:
                  pendingNewCustomerUser.mappedPayload.accountNumber,
                priceFileName:
                  pendingNewCustomerUser.mappedPayload.priceFileName ||
                  priceFileLookup[
                    pendingNewCustomerUser.mappedPayload.priceFileId
                  ] ||
                  'Selected price file',
              }
            : null
        }
      />

      <EditVenueModal
        visible={!!editingVenue}
        venue={editingVenue}
        onCancel={() => setEditingVenue(null)}
        onSubmit={handleEditVenue}
      />

      <VenueUsersModal
        visible={usersModalVisible}
        venue={selectedVenue}
        supplierName={
          account?.supplier?.[0]?.title ||
          account?.supplierTitle ||
          'Your supplier'
        }
        onClose={() => {
          setUsersModalVisible(false)
          setSelectedVenue(null)
        }}
      />

      <LostProductsModal
        visible={lostProductsModalVisible}
        venue={selectedVenueForLostProducts}
        supplierIds={supplierIds}
        onClose={() => {
          setLostProductsModalVisible(false)
          setSelectedVenueForLostProducts(null)
        }}
      />

      <SalesAdvisorModal
        visible={salesAdvisorVisible}
        venue={selectedVenueForSalesAdvisor}
        supplierId={supplierId}
        supplierTitle={
          account?.supplier?.[0]?.title ||
          account?.supplierTitle ||
          'Your supplier'
        }
        onClose={() => {
          setSalesAdvisorVisible(false)
          setSelectedVenueForSalesAdvisor(null)
        }}
      />

      <Modal
        visible={inviteCustomerConfirmVisible}
        title='Confirm invitation — existing OMS user'
        onCancel={resetInviteCustomerState}
        width={560}
        footer={[
          <Button key='cancel' onClick={resetInviteCustomerState}>
            Cancel
          </Button>,
          <Button
            key='confirm'
            type='primary'
            loading={inviteCustomerSubmitting}
            disabled={inviteCustomerSubmitting}
            onClick={sendInviteToExistingCustomer}
          >
            {inviteCustomerSubmitting ? 'Sending…' : 'Send invitation'}
          </Button>,
        ]}
      >
        {pendingInviteCustomer ? (
          <InviteConfirmDetails
            supplierName={
              pendingInviteCustomer.supplierName || supplierDisplayName
            }
            venueName={pendingInviteCustomer.venueName}
            recipientEmail={pendingInviteCustomer.email}
            accountNumber={pendingInviteCustomer.accountNumber}
            priceFileName={
              pendingInviteCustomer.priceFileName || 'Selected price file'
            }
            journey='existing'
          />
        ) : (
          <p>Preparing invitation…</p>
        )}
      </Modal>

      <Modal
        visible={Boolean(existingRelationshipMatch)}
        title='This customer may already be connected'
        onCancel={() => setExistingRelationshipMatch(null)}
        width={560}
        footer={[
          <Button
            key='cancel'
            onClick={() => setExistingRelationshipMatch(null)}
          >
            Cancel
          </Button>,
          <Button
            key='open'
            type='primary'
            onClick={() =>
              openExistingCustomerRecord(existingRelationshipMatch?.customer)
            }
          >
            Open their record
          </Button>,
        ]}
      >
        {existingRelationshipMatch ? (
          <>
            <p style={{ marginTop: 0, color: '#475569', lineHeight: 1.5 }}>
              {INVITE_RECOVERY.ALREADY_CONNECTED}
            </p>
            <p style={{ color: '#475569', lineHeight: 1.5 }}>
              Account number{' '}
              <strong>
                {existingRelationshipMatch.draft?.accountNumber ||
                  existingRelationshipMatch.customer?.accountNumber}
              </strong>
              {existingRelationshipMatch.customer?.venueTitle
                ? ` (${existingRelationshipMatch.customer.venueTitle})`
                : ''}{' '}
              is already linked. We will not create another venue here. Open
              their record to review access, or cancel and use a different
              account number only if this is a separate site.
            </p>
            <p style={{ color: '#64748b', fontSize: 12, lineHeight: 1.45 }}>
              Changing the invitation recipient or transferring ownership is not
              available as a recovery shortcut — contact OMS support if you need
              that help.
            </p>
          </>
        ) : null}
      </Modal>

      <ResendInviteConfirmModal
        visible={resendConfirmVisible}
        draft={resendConfirmDraft}
        loading={inviteResending}
        onCancel={closeResendConfirm}
        onConfirm={confirmResendInvitation}
      />

      <InviteOutcomeModal
        visible={inviteOutcomeVisible}
        outcome={inviteOutcome}
        resending={inviteResending}
        onClose={closeInviteOutcome}
        onViewProgress={handleInviteOutcomeViewProgress}
        onInviteAnother={handleInviteAnotherCustomer}
        onResend={
          inviteOutcome?.emailDispatch === 'failed' &&
          inviteOutcome?.journey === 'existing'
            ? handleResendInviteEmail
            : undefined
        }
      />

      <CustomerProgressDrawer
        visible={progressDrawerVisible}
        customer={progressCustomer}
        orderingStatus={
          progressCustomer ? getVenueStatus(progressCustomer) : null
        }
        onClose={closeCustomerProgress}
        onViewUsers={record => {
          closeCustomerProgress()
          handleOpenUsersModal(record)
        }}
        onEdit={record => {
          closeCustomerProgress()
          setEditingVenue(record)
        }}
        onResend={handleResendFromProgress}
      />
    </Wrapper>
  )
}

const SearchContainer = styled.div`
  position: relative;
  top: 80%;
  margin-top: 30px;
  margin-left: 5px;
`

const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
  background: #ffffff;
  padding: 16px 24px;
  border-radius: 12px;
  margin-top: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);

  @media (max-width: 890px) {
    padding: 12px 16px;
  }
`

const FilterLabel = styled.span`
  font-weight: 600;
  color: #0f172a;
`

const FilterHelper = styled.span`
  font-size: 13px;
  color: #64748b;
`

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Wrapper = styled(FadeIn)`
  width: 100%;
  min-width: 0;

  .restrictShrink {
    white-space: nowrap;
  }

  .insightColumn {
    max-width: 220px;
  }

  .customers-clip-col {
    overflow: hidden;
    max-width: 220px;
  }

  .ant-table-thead > tr > th {
    white-space: nowrap;
  }
`

const CellClip = styled.span`
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const InsightTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 2px 4px 2px 2px;
  border: none;
  background: transparent;
  color: #0369a1;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover span {
    text-decoration: underline;
  }
`

const InfoCardsWrapper = styled.div`
  display: flex;
  gap: var(--sp-space-md);
  flex-wrap: wrap;
  margin-top: var(--sp-space-lg);

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  font-size: 24px;
  line-height: 1;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  z-index: 10;

  &:hover {
    color: #111827;
  }
`

const ProTipCard = styled(Card)`
  position: relative;
  flex: 1;
  min-width: 300px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #fbbf24;
  .ant-card-body {
    padding: 12px 16px;
  }

  @media (max-width: 768px) {
    flex: 1 1 100%;
    min-width: 100%;
  }
`

const ProTipIcon = styled.div`
  float: left;
  margin-right: 12px;
`

const ProTipContent = styled.div`
  overflow: hidden;
`

const ProTipTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #000000;
  margin-bottom: 4px;
`

const ProTipText = styled.div`
  font-size: 13px;
  color: #000000;
  line-height: 1.5;
`

const ProTipBold = styled.span`
  font-weight: 700;
  display: block;
  margin-bottom: 4px;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #ffffff;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;

  @media (min-width: 960px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }

  @media (max-width: 890px) {
    padding: 16px;
    margin-bottom: 16px;
  }
`

const HeroText = styled.p`
  margin: 8px 0 0;
  color: ${({ theme }) => theme.colours.oliveGrey};
  max-width: 560px;
  line-height: 1.5;

  @media (max-width: 700px) {
    font-size: 14px;
  }
`

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;

  @media (max-width: 890px) {
    width: 100%;
  }

  #btn-invite-customer {
    flex-shrink: 0;
  }
`

const ScrollHint = styled.div`
  margin: 0 0 10px;
  font-size: 12px;
  color: #64748b;
`

const TableWrapper = styled.div`
  background: #ffffff;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
  margin-top: 24px;
  width: 100%;
  min-width: 0;
  overflow: hidden;

  @media (max-width: 1024px) {
    padding: 16px;
  }

  /* Only the main body scrolls; antd positions the fixed action pane. */
  .ant-table-scroll > .ant-table-body {
    ${visibleHorizontalScroll}
  }

  .ant-table table {
    table-layout: fixed;
  }

  .ant-table-thead > tr > th {
    background: #f7fafc;
    border-bottom: 1px solid #e2e8f0;
    font-weight: 600;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ant-table-tbody > tr {
    height: 48px;
    &:hover {
      background: #f7fafc;
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #e2e8f0;
    overflow: hidden;
    vertical-align: middle;
  }

  .customer-action-col,
  .customer-action-cell {
    padding-left: 8px !important;
    padding-right: 8px !important;
    white-space: nowrap;
  }

  .customer-action-col .ant-btn-link,
  .customer-action-cell .ant-btn-link {
    padding: 0 2px;
    height: auto;
    font-size: 13px;
  }

  .ant-table-fixed-right {
    box-shadow: -6px 0 8px -6px rgba(15, 23, 42, 0.22);
  }

  .ant-table-fixed-right .ant-table-thead > tr > th,
  .ant-table-fixed-right .ant-table-tbody > tr > td {
    background: #fff;
  }

  .ant-table-fixed-right .ant-table-tbody > tr:hover > td {
    background: #f7fafc;
  }
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  color: #64748b;
  font-size: 15px;
  padding: 48px 24px;
  max-width: 420px;
  margin: 0 auto;
`

const EmptyStateButton = styled.button`
  margin-top: 8px;
  background-color: ${({ theme }) => theme.colours.lettuceGreen};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 0.9;
  }
`

const CTAButton = styled.button`
  background-color: ${({ theme }) => theme.colours.lettuceGreen};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 0.9;
  }
`

const InfoButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #f4f5f7;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  cursor: pointer;
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-weight: 500;
`

const ZeroBadge = styled.div`
  height: auto;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: #b5d922;
  border: none;
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  cursor: default;
  user-select: none;
  margin: 0;
  display: inline-block;
  text-align: center;
  box-shadow: none;
`

const BadgeButton = styled(Button).attrs(props => ({
  style: {
    backgroundColor: props.$badgeColor || '#dc2626',
    borderColor: props.$badgeColor || '#dc2626',
  },
}))`
  height: auto;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: ${props => props.$badgeColor || '#dc2626'};
  border: none;
  color: #ffffff !important;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  margin: 0;
  box-shadow: none;

  &:hover,
  &:focus {
    background-color: ${props =>
      props.$badgeHoverColor || '#b91c1c'} !important;
    border: none !important;
    color: #ffffff !important;
    opacity: 0.9;
    transform: scale(1.05);
    box-shadow: none;
  }

  &:active {
    background-color: ${props => {
      const baseColor = props.$badgeColor || '#dc2626'
      return baseColor === '#dc2626' ? '#991b1b' : '#b45309' // darker red or darker amber
    }} !important;
    border: none !important;
    color: #ffffff !important;
    transform: scale(0.98);
    box-shadow: none;
  }
`

export default Customers
